import React, { Fragment, useState, useEffect, useLayoutEffect, useReducer, useRef, useMemo } from 'react';
import { Button, Typography, Checkbox, Box } from '@mui/material';
import moment from 'moment-timezone';
import { connect } from 'react-redux';
import { toast } from 'react-toastify';
import { mergeDialog, toggleDialog } from '../../../store/control/duck';
import { withRouter } from 'react-router';
import { injectIntl } from 'react-intl';
import MetControl from '../controls/MetControl/MetControl';
import OpportunityControl from '../controls/OpportunityControl/OpportunityControl';
import BusinessVolControl from '../controls/BusinessVolControl/BusinessVolControl';
import RemindDateControl from '../controls/RemindDateControl/RemindDateControl';
import { ReminderLoadDay, ReminderLoadHint } from '../controls/RemindDateControl/ReminderLoad';
import { reminderLoadByDay } from '../../../views/Advisor/ClientV2/reminderCalendar';
import ReminderActiveControl from '../controls/ReminderActiveControl/ReminderActiveControl';
import RemarkControl from '../controls/RemarkControl/RemarkControl';
import TagsControl from '../controls/TagsControl/TagsControl';
import { enableTracking, initCreatePhase, submitClient } from '../../../store/client/duck';
import LatestMeetingDateControl from '../controls/LatestMeetingDateControl/LatestMeetingDateControl';
import { isSubscriptionAllowAction } from '../../../utils/user-subscription-store';
import { getClientConnectionStatus, getClientEmail, goToFactsheet } from '../utils';
import LocationControl from '../controls/LocationControl/LocationControl';
import SwipeableDrawer from '@mui/material/SwipeableDrawer';
import { ThemeProvider } from '@mui/material/styles';
import TodoList from '../../TodoList/TodoList';
import Template from '../../Template/Template';
import DoneOutlineRoundedIcon from '@mui/icons-material/DoneOutlineRounded';
import EventAvailableRoundedIcon from '@mui/icons-material/EventAvailableRounded';
import FolderSync from 'lucide-react/dist/esm/icons/folder-sync';
import { addAdviserTodo } from '../../../store/todoList/duck';
import { getFactsheet } from '../../../store/client/api';
import './MeetingDialog.scss';
import Grid from '@mui/material/Grid';
import _ from 'lodash';
import { DatePicker } from '../../ClickableField/ClickableField';
import { Capacitor } from '@capacitor/core';
import { Preferences } from '@capacitor/preferences';
import NextMeetingDateControl from '../controls/NextMeetingDateControl/NextMeetingDateControl';
import BottomSheetOrDialog from '../../BottomSheetOrDialog/BottomSheetOrDialog';
import { LOADING, SUCCESS, ERROR } from '../../../constants/phase';
import LoadingBackdrop from '../../LoadingBackdrop/LoadingBackdrop';
import { usePrevious, useWindowSize } from '../../../utils/hooks';
// import RecordMeetingRemarkButton from '../../RecordMeetingRemarkButton/RecordMeetingRemarkButton';
import { shouldEnableSpeechToText } from '../../../utils/user';
import { hasCampaignCode } from '../../../utils/user';
import { trackDirect } from '../../../store/analytics/api';
import { aiGenerateWithTextStreaming } from '../../../store/openai/api';
import { createMeetingNoteFactsheetSyncRunner } from '../../../utils/meeting-note-factsheet-sync';
import { addEventToCalendarPrompt } from '../../../utils/native/calendar';
import IconToast from '../../../components/NewToast';
import { clientPageVersion, clientPath } from '../../../utils/client-route';
import ChooseDefaultCalendarDialog from '../../../components/ChooseDefaultCalendarDialog/ChooseDefaultCalendarDialog';
import MeetingFactsheetSyncToast from './MeetingFactsheetSyncToast';

import { withWidth } from '../../../utils/withWidth';

/**
 * The ToDo and Template drawers open ON TOP of this dialog, so they have to
 * outrank it. Both used to sit at 1300 — the SAME value MUI gives a modal — and
 * neither is a child of the dialog, so a tie was settled by DOM order and the
 * dialog won: pressing Add to Todo opened the drawer behind the form that
 * opened it.
 *
 * 1350 is deliberately between MUI's `modal` (1300) and `snackbar` (1400): above
 * every dialog, still below the toasts that report what these drawers did.
 *
 * It has to go on the drawer ROOT as well as the paper. At 800px and below the
 * drawers are `temporary`, i.e. a Modal whose root is `position: fixed;
 * z-index: 1200` — a stacking context — so a paper z-index only ranks inside
 * that 1200 and the whole drawer still opened behind the dialog on phones.
 * On wider screens the `persistent` root is not positioned, so the root value
 * is inert there and the paper's own z-index does the work.
 */
const MEETING_DRAWER_Z_INDEX = 1350;

/**
 * Raising the drawers to 1350 puts them above MUI's modal layer (1300), which
 * is also where every Menu, Popover and Dialog opened from INSIDE them lives —
 * TodoMenu (⋮), TodoDialog's delete-all confirm, the note pad, TemplateMenu,
 * TemplateDialog. Left alone those open underneath the drawer: an invisible
 * menu, or a focus-trapped confirm nobody can see.
 *
 * So the drawer contents get a theme whose modal layer sits above the drawer.
 * It is scoped to these two drawers' children only (portals keep React context,
 * so their popups inherit it); TodoList and Template elsewhere — the top bar,
 * the add-client form — still render with the app theme, where their drawers
 * sit at the default layer and 1300 already wins. Stays below MUI's tooltip
 * layer (1500). The one popup outside this subtree, the global whiteboard
 * dialog, is handled by closing these drawers when it opens (see below).
 */
const raiseModalLayerAboveDrawer = outerTheme => ({
  ...outerTheme,
  zIndex: { ...outerTheme.zIndex, modal: MEETING_DRAWER_Z_INDEX + 50 }
});

/**
 * Mount the ToDo / Template lists only while their drawer is open.
 *
 * SwipeableDrawer forces `keepMounted: true` on its `temporary` (phone/tablet)
 * variant so swipe-to-open has a paper to measure. Both drawers here set
 * `disableSwipeToOpen`, so that buys nothing — but it meant TodoList and
 * Template were mounted, hidden, every time this dialog opened (MeetingDialog
 * renders null when closed, so it is a fresh mount on each open), and were
 * re-rendered on every dialog render because their close/open callbacks are new
 * each time. Measured on the phone sheet: about half of this dialog's render
 * work on open went to those two invisible lists.
 *
 * `keepMounted: false` is MUI's own switch: content mounts when a drawer opens
 * and unmounts only after its close transition, so the slide-out still shows
 * the list. Neither list loads data on mount (TodoList/Template only focus
 * themselves), so nothing arrives later than before. The `persistent` variant
 * used above 800px has no Modal and is unaffected.
 */
const DRAWER_MODAL_PROPS = { keepMounted: false };

const isSummaryMode = mode => mode === 'summary';

const formatMeetingDate = value => (value ? moment(value).format('YYYY-MM-DD') : null);

const getMeetingNotesSnapshot = ({
  remark,
  tags,
  latestMeetingDate,
  nextMeetingDate,
  lastMeetingLocation,
  fsReminderActive,
  fsReminderDate,
  isRecruitment
}) => ({
  remark: remark || '',
  tags: JSON.stringify(tags || []),
  latestMeetingDate: formatMeetingDate(latestMeetingDate),
  nextMeetingDate: formatMeetingDate(nextMeetingDate),
  lastMeetingLocation: lastMeetingLocation || '',
  fsReminderActive: !!fsReminderActive,
  fsReminderDate: formatMeetingDate(fsReminderDate),
  isRecruitment: !!isRecruitment
});

const INITIAL_STATE = {
  businessVolError: undefined,
  reminderDateError: undefined,
  width: window.innerWidth,
  todoOpen: false,
  templateOpen: false,
  loading: false
};

const MeetingDialog = props => {
  const {
    intl,
    history,
    userDetails,
    teamCampaignCodes,
    meetingDialog,
    createClientPhase,
    getClientByIdPhase,
    getUserPhase,
    enableTracking,
    submitClient,
    initCreatePhase,
    toggleDialog,
    mergeDialog,
    addAdviserTodo,
    userSubscription,
    clientData,
    myWhiteBoardDialog
  } = props;

  const {
    client,
    mode,
    met,
    opportunity,
    businessVol,
    fsReminderDate,
    fsReminderActive,
    latestMeetingDate,
    trackingStartDate,
    lastMeetingLocation,
    remark,
    tags,
    isRecruitment,
    nextMeetingDate,
    closed,
    triggerRecordDialog,
    // Which field the caller opened this dialog *for*. ClientV2's 上次會面
    // readout sets it to 'latestMeetingDate' and its 提醒 readout to
    // 'fsReminderDate', so the adviser lands looking at the row they tapped,
    // instead of hunting for it in a form of eight. Only the one row is marked;
    // nothing else about the dialog changes, and callers that omit it get
    // exactly what they got before.
    focusField
  } = meetingDialog;

  const [businessVolError, setBusinessVolError] = useState(undefined);
  const [reminderDateError, setReminderDateError] = useState(undefined);
  const [todoOpen, setTodoOpen] = useState(false);
  const [templateOpen, setTemplateOpen] = useState(false);
  const [loading, setLoading] = useState(false);
  const [meetingFactsheetSyncing, setMeetingFactsheetSyncing] = useState(false);
  const [addToCalendar, setAddToCalendar] = useState(false);
  const [chooserDefaultCalendarDialogOpen, setChooserDefaultCalendarDialogOpen] = useState(false);
  const [innerWidth, innerHeight] = useWindowSize();
  const prevCreateClientPhase = usePrevious(createClientPhase);
  const prevGetClientByIdPhase = usePrevious(getClientByIdPhase);
  const prevGetUserPhase = usePrevious(getUserPhase);
  const ref = useRef(null);
  const recordMeetingRemarkButtonRef = useRef(null);
  const closeAfterSaveRef = useRef(true);
  const pendingAfterSaveRef = useRef(null);
  const resolvedReminderStateRef = useRef({ fsReminderActive: false, fsReminderDate: null });
  const savedMeetingNotesSnapshotRef = useRef(null);
  const meetingFactsheetSyncRunnerRef = useRef(null);
  const meetingFactsheetSyncingRef = useRef(false);
  const [shouldTrackPotentialRecruitment, setShouldTrackPotentialRecruitment] = useState(false);

  if (!meetingFactsheetSyncRunnerRef.current) {
    meetingFactsheetSyncRunnerRef.current = createMeetingNoteFactsheetSyncRunner({
      analyze: aiGenerateWithTextStreaming
    });
  }
  // TodoList's whiteboard button opens the app-level whiteboard dialog, which
  // renders outside these drawers and so at the ordinary modal layer — below the
  // drawers' 1350. Close the drawers when it opens so the whiteboard is not
  // hidden behind the list that launched it.
  const whiteboardOpen = !!myWhiteBoardDialog && !myWhiteBoardDialog.closed;
  useEffect(() => {
    if (!whiteboardOpen) return;
    setTodoOpen(false);
    setTemplateOpen(false);
  }, [whiteboardOpen]);

  const getCurrentMeetingNotesSnapshot = () =>
    getMeetingNotesSnapshot({
      remark,
      tags,
      latestMeetingDate,
      nextMeetingDate,
      lastMeetingLocation,
      fsReminderActive,
      fsReminderDate,
      isRecruitment
    });

  // The sync strip renders off the saved snapshot, so a snapshot change has to re-render.
  const [, bumpSavedSnapshot] = useReducer(n => n + 1, 0);
  const updateSavedMeetingNotesSnapshot = snapshot => {
    savedMeetingNotesSnapshotRef.current = snapshot;
    bumpSavedSnapshot();
  };

  // Layout, not passive: the first paint would otherwise show the note as unsaved.
  useLayoutEffect(() => {
    if (!meetingDialog || closed || isSummaryMode(mode)) {
      return;
    }

    updateSavedMeetingNotesSnapshot(getCurrentMeetingNotesSnapshot());
  }, [client?._id, closed, mode]);

  const hasUnsavedMeetingNotes = !_.isEqual(getCurrentMeetingNotesSnapshot(), savedMeetingNotesSnapshotRef.current);

  const enableSpeechToText = shouldEnableSpeechToText({
    user: userDetails,
    teamCampaignCodes: teamCampaignCodes,
    userSubscription: userSubscription
  });

  const runMeetingFactsheetSync = async ({ clientId, factsheet, remark: savedRemark, forceRetry = false }) => {
    // Only the failure toast outlives the run: progress is the full-screen
    // LoadingBackdrop, so there is no toast to update while analysing.
    let errorToastId;
    const dismissErrorToast = () => {
      if (errorToastId !== undefined && errorToastId !== null) toast.dismiss(errorToastId);
    };

    const retry = () => {
      dismissErrorToast();
      toggleDialog('meetingFactsheetSync', false);
      runMeetingFactsheetSync({ clientId, factsheet, remark: savedRemark, forceRetry: true });
    };

    meetingFactsheetSyncingRef.current = true;
    setMeetingFactsheetSyncing(true);
    let result;
    try {
      result = await meetingFactsheetSyncRunnerRef.current.run({
        clientId,
        factsheet,
        remark: savedRemark,
        forceRetry,
        enabled: !isSummaryMode(mode) && !!hasCampaignCode(userDetails, teamCampaignCodes, 'FEATURE_TEST')
      });
    } finally {
      meetingFactsheetSyncingRef.current = false;
      setMeetingFactsheetSyncing(false);
    }
    if (result.status !== 'success' && result.status !== 'error') return;

    if (result.status === 'success') {
      const { suggestions } = result;
      trackDirect('meetingFactsheetSyncAnalysisSuccess', { differenceCount: suggestions.length });
      if (!suggestions.length) {
        toast.info(IconToast('info', intl.formatMessage({ id: 'meeting-factsheet-sync-no-changes' })), {
          className: 'new-toast'
        });
        return;
      }

      toast.info(IconToast('success', intl.formatMessage({ id: 'meeting-factsheet-sync-complete' })), {
        className: 'new-toast'
      });
      toggleDialog('meetingFactsheetSync', {
        clientId,
        factsheetId: factsheet?._id,
        suggestions,
        // Without this the open dialog keeps its pre-sync dates and the next Save writes them back.
        onApplied: updated => {
          const latest = updated.latestMeetingDate ? moment.parseZone(updated.latestMeetingDate) : null;
          const next = updated.nextMeetingDate ?? null;
          updateSavedMeetingNotesSnapshot({
            ...savedMeetingNotesSnapshotRef.current,
            latestMeetingDate: formatMeetingDate(latest),
            nextMeetingDate: formatMeetingDate(next)
          });
          mergeDialog('meeting', { latestMeetingDate: latest, nextMeetingDate: next, client: { factsheetId: updated } });
        }
      });
      return;
    }

    trackDirect('meetingFactsheetSyncAnalysisFailure', { message: result.error?.message });
    errorToastId = toast.error(
      <MeetingFactsheetSyncToast
        message={intl.formatMessage({ id: 'meeting-factsheet-sync-error' })}
        retryLabel={intl.formatMessage({ id: 'meeting-factsheet-sync-retry' })}
        closeLabel={intl.formatMessage({ id: 'Close' })}
        onRetry={retry}
        onClose={dismissErrorToast}
      />,
      {
        className: 'new-toast meeting-factsheet-sync-toast',
        autoClose: false,
        closeOnClick: false,
        draggable: false
      }
    );
    toggleDialog('meetingFactsheetSync', {
      clientId,
      factsheetId: factsheet?._id,
      analysisError: true,
      onRetry: retry
    });
  };

  useEffect(() => {
    if (!pendingAfterSaveRef.current) {
      return;
    }

    if (createClientPhase === true) {
      if (
        (getClientByIdPhase === SUCCESS && prevGetClientByIdPhase === LOADING && getUserPhase !== LOADING) ||
        (getUserPhase === true && prevGetUserPhase === LOADING && getClientByIdPhase !== LOADING)
      ) {
        setTimeout(async () => {
          setLoading(false);
          toast.info(IconToast('success', intl.formatMessage({ id: 'meeting-notes-saved' })), {
            className: 'new-toast'
          });
          const { fsReminderActive: savedReminderActive, fsReminderDate: savedReminderDate } =
            resolvedReminderStateRef.current;
          updateSavedMeetingNotesSnapshot(
            getMeetingNotesSnapshot({
              remark,
              tags,
              latestMeetingDate,
              nextMeetingDate,
              lastMeetingLocation,
              fsReminderActive: savedReminderActive,
              fsReminderDate: savedReminderDate,
              isRecruitment
            })
          );
          if (closeAfterSaveRef.current) {
            mergeDialog('meeting', { closed: true });
            if (addToCalendar) {
              const result = await handleAddToCalendar();
              if (!result) {
                toast.error(IconToast('error', intl.formatMessage({ id: 'unable-to-add-meeting-notes-to-calendar' })), {
                  className: 'new-toast',
                  autoClose: 8000
                });
              }
              setAddToCalendar(false);
            }
          } else {
            mergeDialog('meeting', {
              ...meetingDialog,
              fsReminderActive: savedReminderActive,
              fsReminderDate: savedReminderDate,
              client: {
                ...client,
                factsheetId: {
                  ...client.factsheetId,
                  remark,
                  tags,
                  isRecruitment,
                  latestMeetingDate,
                  nextMeetingDate,
                  lastMeetingLocation,
                  fsReminderActive: savedReminderActive,
                  fsReminderDate: savedReminderDate
                }
              }
            });
          }
          if (pendingAfterSaveRef.current?.resolve) pendingAfterSaveRef.current.resolve();
          pendingAfterSaveRef.current = null;
          closeAfterSaveRef.current = true;
          initCreatePhase();
        }, 300);
      }
    }
  }, [
    meetingDialog,
    createClientPhase,
    getClientByIdPhase,
    getUserPhase,
    prevGetClientByIdPhase,
    prevGetUserPhase,
    mergeDialog,
    initCreatePhase
  ]);

  useEffect(() => {
    if ((createClientPhase === false || createClientPhase === ERROR) && pendingAfterSaveRef.current) {
      setLoading(false);
      toast.error(IconToast('error', intl.formatMessage({ id: 'unable-to-save-meeting-notes' })), {
        className: 'new-toast'
      });
      pendingAfterSaveRef.current.reject(new Error('save failed'));
      pendingAfterSaveRef.current = null;
      closeAfterSaveRef.current = true;
      setAddToCalendar(false);
    }
  }, [createClientPhase, intl]);

  useEffect(() => {
    if (triggerRecordDialog) {
      setTimeout(() => {
        const instance = recordMeetingRemarkButtonRef.current?._wrappedInstance;
        if (instance) {
          instance.openDialog();
          mergeDialog('meeting', { triggerRecordDialog: false });
        }
      }, 1100);
    }
  }, [triggerRecordDialog, mergeDialog]);

  const handleAddToCalendar = async () => {
    let start = nextMeetingDate ? moment.tz(nextMeetingDate, 'Asia/Hong_Kong').startOf('day').valueOf() : null;
    let end = nextMeetingDate ? moment(start).add(1, 'day').valueOf() : null;
    let title = intl.formatMessage({ id: 'meeting-with-client-name' }, { name: client.factsheetId.name });
    let location = lastMeetingLocation || '';
    let baseDetails = remark ? `${remark}\n\n` : '';
    let linkText = intl.formatMessage({ id: 'view-more-client-details-in-portfoplus' });
    let result = await addEventToCalendarPrompt(
      title,
      start,
      end,
      nextMeetingDate ? true : false,
      location,
      `${baseDetails}${linkText}`
    );
    return result;
  };

  const close = () => {
    if (meetingFactsheetSyncingRef.current) return;

    if (isSummaryMode(mode)) {
      toggleDialog('discardWarning', {
        onConfirm: () => {
          toggleDialog('meeting', { ...meetingDialog, closed: true });
        }
      });
      return;
    }

    const hasUnsavedChanges = !_.isEqual(getCurrentMeetingNotesSnapshot(), savedMeetingNotesSnapshotRef.current);

    if (hasUnsavedChanges) {
      toggleDialog('discardWarning', {
        onConfirm: () => {
          toggleDialog('meeting', { ...meetingDialog, closed: true });
        }
      });
      return;
    }

    toggleDialog('meeting', { ...meetingDialog, closed: true });
  };
  const onExited = () => {
    toggleDialog('meeting', false);
    setBusinessVolError(undefined);
    setReminderDateError(undefined);
    setTodoOpen(false);
    setTemplateOpen(false);
    setLoading(false);
  };

  const changeReminderDate = date => {
    setReminderDateError(undefined);
    toggleDialog('meeting', {
      ...meetingDialog,
      fsReminderDate: date,
      fsReminderActive: true
    });
  };

  const changeReminderActive = checked => {
    if (!isSubscriptionAllowAction({ type: 'core', nicknames: ['Pro', 'Ultra'] })) return;

    setReminderDateError(undefined);
    toggleDialog('meeting', {
      ...meetingDialog,
      fsReminderDate: checked ? fsReminderDate : null,
      fsReminderActive: checked
    });
  };

  const toggleIsRecruitment = value => {
    toggleDialog('meeting', { ...meetingDialog, isRecruitment: value });
    const ori = _.get(client, 'factsheetId.isRecruitment', false);
    if (!ori && value) {
      setShouldTrackPotentialRecruitment(true);
    } else {
      setShouldTrackPotentialRecruitment(false);
    }
  };

  const chooseCalendarAndSubmit = async () => {
    setAddToCalendar(true);
    let { value: defaultCalendar } = await Preferences.get({ key: 'defaultCalendar' });
    if (Capacitor.getPlatform() !== 'ios' || defaultCalendar) {
      submit();
    } else {
      setChooserDefaultCalendarDialogOpen(true);
    }
  };

  const buildMeetingNotesFormData = ({ relaxReminderValidation = false } = {}) => {
    const formData = {
      clientId: client._id,
      email: getClientEmail(client),
      advisorId: userDetails._id,
      tags: tags,
      remark: remark,
      isRecruitment
    };

    let reminderActive = fsReminderActive;
    let reminderDate = fsReminderDate;

    if (relaxReminderValidation && reminderActive && !reminderDate) {
      reminderActive = false;
      reminderDate = null;
    }

    resolvedReminderStateRef.current = {
      fsReminderActive: reminderActive,
      fsReminderDate: reminderActive ? reminderDate : null
    };

    if (reminderActive && reminderDate) {
      formData.fsReminderActive = true;
      formData.fsReminderDate = moment.utc(moment(reminderDate).format('YYYY-MM-DD')).format('YYYY-MM-DDT00:00:00.00Z');
    } else {
      formData.fsReminderActive = false;
      formData.fsReminderDate = null;
    }

    if (latestMeetingDate) {
      formData.latestMeetingDate = moment
        .utc(moment(latestMeetingDate).format('YYYY-MM-DD'))
        .format('YYYY-MM-DDT00:00:00.00Z');
    } else {
      formData.latestMeetingDate = null;
    }

    if (nextMeetingDate) {
      formData.nextMeetingDate = moment
        .utc(moment(nextMeetingDate).format('YYYY-MM-DD'))
        .format('YYYY-MM-DDT00:00:00.00Z');
    } else {
      formData.nextMeetingDate = null;
    }

    formData.lastMeetingLocation = lastMeetingLocation;

    const old_data = client.factsheetId.tags ? client.factsheetId.tags : [];
    const new_data = tags ? tags : [];
    formData.usedTags = {
      old_data: old_data,
      new_data: new_data
    };

    return formData;
  };

  const saveMeetingNotes = ({ closeOnSuccess = false } = {}) => {
    if (!meetingDialog || isSummaryMode(mode)) {
      return Promise.resolve();
    }

    const relaxReminderValidation = !closeOnSuccess;

    if (closeOnSuccess && fsReminderActive && !fsReminderDate) {
      setReminderDateError(intl.formatMessage({ id: 'Please set date' }));
      return Promise.reject(new Error('validation'));
    }

    if (relaxReminderValidation && fsReminderActive && !fsReminderDate) {
      setReminderDateError(undefined);
      toggleDialog('meeting', {
        ...meetingDialog,
        fsReminderActive: false,
        fsReminderDate: null
      });
    }

    if (pendingAfterSaveRef.current) {
      pendingAfterSaveRef.current.reject(new Error('save superseded'));
    }

    return new Promise((resolve, reject) => {
      const formData = buildMeetingNotesFormData({ relaxReminderValidation });
      closeAfterSaveRef.current = closeOnSuccess;
      pendingAfterSaveRef.current = { resolve, reject };
      setLoading(true);
      submitClient(formData);
      trackDirect('editMeetingNote');
      if (shouldTrackPotentialRecruitment) {
        const trackAddedPotentialRecruitment = require('../../../utils/tracking/index').trackAddedPotentialRecruitment;
        trackAddedPotentialRecruitment({
          'Facesheet ID': _.get(client, 'factsheetId._id', false),
          Source: _.get(client, 'factsheetId.source', false),
          'Create Date': _.get(client, 'factsheetId.createdate', false),
          'Latest Meeting Date': formData.latestMeetingDate
        });
      }
    });
  };

  // The preview's current values become the server's `expected` guard, so they
  // must come from the saved factsheet, not the snapshot this dialog opened with
  // — hence the implicit save first: sync reads what is in the database.
  const syncMeetingNotesToFactsheet = async () => {
    if (meetingFactsheetSyncingRef.current || isSummaryMode(mode)) return;

    // Before the save, not after: the save path's own 300ms setLoading(false)
    // would otherwise blink the overlay off between saving and analysing.
    meetingFactsheetSyncingRef.current = true;
    setMeetingFactsheetSyncing(true);

    try {
      if (hasUnsavedMeetingNotes) {
        try {
          await saveMeetingNotes({ closeOnSuccess: false });
        } catch {
          return;
        }
      }

      const response = await getFactsheet(client.factsheetId._id).catch(() => null);
      const savedFactsheet = response?.success ? response.data : null;
      if (!savedFactsheet) {
        toast.error(IconToast('error', intl.formatMessage({ id: 'meeting-factsheet-sync-update-error' })), {
          className: 'new-toast'
        });
        return;
      }

      // forceRetry: a manual tap must re-analyse even if this note was analysed earlier in the session.
      await runMeetingFactsheetSync({
        clientId: client._id,
        factsheet: savedFactsheet,
        remark: savedFactsheet.remark,
        forceRetry: true
      });
    } finally {
      meetingFactsheetSyncingRef.current = false;
      setMeetingFactsheetSyncing(false);
    }
  };

  const runMeetingNotesAiAction = async action => {
    const hasUnsavedChanges = !_.isEqual(getCurrentMeetingNotesSnapshot(), savedMeetingNotesSnapshotRef.current);

    if (hasUnsavedChanges) {
      try {
        await saveMeetingNotes({ closeOnSuccess: false });
      } catch {
        return;
      }
    }

    if (action) action();
  };

  const submit = async () => {
    if (!meetingDialog || meetingFactsheetSyncingRef.current) return;

    const summaryMode = isSummaryMode(mode);

    if (summaryMode) {
      if (!businessVol) {
        setBusinessVolError(intl.formatMessage({ id: 'Required input field' }));
        return;
      }

      const obj = {};
      obj.businessVol = businessVol;
      obj.trackingId = client.trackingId._id;
      obj.met = met;
      obj.opportunity = opportunity;
      const formattedStartDate = trackingStartDate
        ? moment.utc(moment(trackingStartDate).format('YYYY-MM-DD')).format('YYYY-MM-DDT00:00:00.00Z')
        : '';
      obj.startDate = formattedStartDate;
      if (opportunity === 'H') {
        const date_high = moment().format('YYYY-MM-DD');
        const startDate1 = client.trackingId.startDate;
        obj.dateHigh = moment.utc(date_high).format('YYYY-MM-DDT00:00:00.00Z');
        obj.dayCount = moment().diff(moment(startDate1, 'YYYY-MM-DD'), 'days');
      }
      obj.clientId = client._id;
      enableTracking(obj);
      toggleDialog('meeting', { ...meetingDialog, closed: true });
    }

    if (!summaryMode) {
      try {
        await saveMeetingNotes({ closeOnSuccess: true });
      } catch {
        return;
      }
    }
  };

  const factsheet = () => {
    if (meetingFactsheetSyncingRef.current) return;
    const client = { ..._.get(meetingDialog, 'client') };
    setAddToCalendar(false);
    submit();
    goToFactsheet(client, history);
  };

  const closeCase = () => {
    if (meetingFactsheetSyncingRef.current) return;
    const client = { ..._.get(meetingDialog, 'client') };
    const connectionStatus = getClientConnectionStatus(client);

    if (connectionStatus === 'Disconnected') {
      return;
    }

    if (!client.trackingId || !client.trackingId.isActive) {
      toggleDialog('trackOn', { client: client, startDate: moment(), expectedBusinessAmount: '' });
    } else {
      toggleDialog('trackOff', {
        client: client,
        businessVol: _.get(client, 'trackingId.businessVol', null),
        met: _.get(client, 'trackingId.met', 0)
      });
    }
    toggleDialog('meeting', { ...meetingDialog, closed: true });
  };

  const openTodo = () => setTodoOpen(true);
  const closeTodo = () => setTodoOpen(false);
  const openTemplate = () => {
    setTodoOpen(false);
    setTemplateOpen(true);
  };
  const closeTemplate = () => setTemplateOpen(false);

  // "See on calendar" — the reminder row already carries one secondary action
  // (Add to Todo), and this is the other: stop editing the date, go look at it
  // among the rest of the month.
  //
  // V2 only: the calendar does not exist on V1 yet (spec D11, phase 2). The
  // button used to send V1 advisers to `/client` with select: 'allReminder',
  // but clientPage.js reads location.select only in UNSAFE_componentWillMount —
  // pushed from /client itself, that remounts nothing, and the tap just closed
  // the dialog. Hiding it keeps V1 unchanged until V1 gets the calendar.
  //
  // Both reminder actions answer to one rule: there is nothing to add to a todo
  // list, and nothing to show on a calendar, until the date is both set and
  // switched on.
  const showSeeOnCalendar = clientPageVersion() === 'v2';
  const reminderActionsEnabled = Boolean(fsReminderDate && fsReminderActive);

  // One day key for the hint and the calendar hand-off. parseZone, like
  // RemindDateControl and reminderOf: the factsheet stores the reminder as UTC
  // midnight ('…T00:00:00.00Z'), which a plain moment() turns into the previous
  // day on a device west of UTC.
  const reminderDayKey = reminderActionsEnabled ? moment.parseZone(fsReminderDate).format('YYYY-MM-DD') : null;

  // Who else already has a reminder on each day, for the dots in the date picker
  // and the line under the field. Read off the client list the page has already
  // loaded; when nothing is loaded there is simply nothing to mark.
  const clientId = client && client._id;
  const userEmail = userDetails && userDetails.email;
  // This component is mounted app-wide with `meetingDialog` null until a note is
  // opened, and `closed` is undefined then — so test both, or every client-list
  // update rescans the whole book for a dialog nobody has open.
  const isDialogOpen = Boolean(meetingDialog) && !closed;
  const reminderLoad = useMemo(
    () => (isDialogOpen ? reminderLoadByDay(clientData || [], userEmail, moment(), clientId) : new Map()),
    [isDialogOpen, clientData, userEmail, clientId]
  );
  const reminderDayLoad = reminderDayKey ? reminderLoad.get(reminderDayKey) : undefined;

  // Save first, then leave. Closing directly would skip close()'s discard guard
  // and silently drop an unsaved remark or date — and the date is the very thing
  // the adviser wants to see: unsaved, the calendar would not show this client.
  // Same pattern as runMeetingNotesAiAction; a failed save keeps the dialog open.
  const seeOnCalendar = async () => {
    if (!reminderDayKey) return;
    const hasUnsavedChanges = !_.isEqual(getCurrentMeetingNotesSnapshot(), savedMeetingNotesSnapshotRef.current);
    if (hasUnsavedChanges) {
      try {
        await saveMeetingNotes({ closeOnSuccess: false });
      } catch {
        return;
      }
    }
    // mergeDialog, not toggleDialog with this render's meetingDialog: after an
    // awaited save that object is stale, and spreading it would put the
    // pre-save values back into the store.
    mergeDialog('meeting', { closed: true });
    history.push({ pathname: clientPath(), calendarDay: reminderDayKey });
  };

  const addToTodo = () => {
    setTodoOpen(true);
    setTimeout(() => {
      if (fsReminderDate && fsReminderActive) {
        addAdviserTodo({
          todo: {
            todo: client.factsheetId.name,
            important: false,
            dueDate: fsReminderDate,
            completed: false
          }
        });
      }
    }, 200);
  };

  // const appendRemark = text => {
  //   toggleDialog('meeting', {
  //     ...meetingDialog,
  //     remark: `${remark}${remark ? `\n${!(remark || '').endsWith('\n') ? '\n' : ''}` : ''}${text}`
  //   });
  // };

  if (!meetingDialog) return null;

  const summaryMode = isSummaryMode(mode);
  const iOS = process.browser && /iPad|iPhone|iPod/.test(navigator.userAgent);

  const header = intl.formatMessage({ id: summaryMode ? 'Follow Up' : 'meetingNotes' });

  const actionsPrepend = [
    !summaryMode ? (
      <Button onClick={factsheet} color="primary">
        {intl.formatMessage({ id: 'Factsheet' })}
      </Button>
    ) : (
      <Button variant="outlined" onClick={closeCase} color="primary">
        {intl.formatMessage({ id: 'Case Closing' })}
      </Button>
    )
  ];

  const actions = [
    ...(summaryMode
      ? []
      : [
          <Button variant="outlined" onClick={chooseCalendarAndSubmit} color="primary" autoFocus>
            {intl.formatMessage({ id: 'save-and-add-to-calendar' })}
          </Button>
        ]),
    <Button
      onClick={() => {
        setAddToCalendar(false);
        submit();
      }}
      color="primary"
      autoFocus
    >
      {intl.formatMessage({ id: 'Save' })}
    </Button>
  ].filter(Boolean);

  const isWidescreen = innerWidth > 800;

  const remarkControlContent = (
    <RemarkControl
      data-body-scroll-lock-ignore="true"
      remark={remark}
      onChange={event =>
        toggleDialog('meeting', {
          ...meetingDialog,
          remark: event.target.value
        })
      }
      meetingDialog={meetingDialog}
      recordMeetingRemarkButtonRef={recordMeetingRemarkButtonRef}
      onRunMeetingNotesAiAction={runMeetingNotesAiAction}
      enableSpeechToText={enableSpeechToText}
      setLoading={setLoading}
      isRecruitment={isRecruitment}
      style={{ marginTop: 0, marginBottom: 0, flexGrow: 1, minHeight: 0 }}
      sx={{
        height: '100%',
        '& .MuiInputBase-root': { height: '100%', alignItems: 'flex-start' },
        '& textarea': { height: '100% !important', overflowY: 'auto !important' }
      }}
    />
  );

  // Unsaved is no longer a state of its own: one tap saves and then syncs, so a
  // dirty note is as ready as a clean one — it only changes what the strip says.
  const syncState = meetingFactsheetSyncing ? 'syncing' : remark?.trim() ? 'ready' : 'empty';
  const syncHintId =
    syncState === 'ready' && hasUnsavedMeetingNotes
      ? 'meeting-factsheet-sync-hint-unsaved'
      : `meeting-factsheet-sync-hint-${syncState}`;
  const syncActionId =
    syncState === 'syncing'
      ? 'meeting-factsheet-sync-analyzing'
      : hasUnsavedMeetingNotes
      ? 'meeting-factsheet-sync-save-and-sync'
      : 'meeting-factsheet-sync-action';
  const factsheetSyncStrip = !summaryMode && hasCampaignCode(userDetails, teamCampaignCodes, 'FEATURE_TEST') && (
    <div className="meeting-factsheet-sync-strip">
      <span className="meeting-factsheet-sync-strip__icon" aria-hidden="true">
        <FolderSync size="1em" aria-hidden="true" />
      </span>
      <div className="meeting-factsheet-sync-strip__text">
        <div className="meeting-factsheet-sync-strip__title">
          {intl.formatMessage({ id: 'meeting-factsheet-sync-title' })}
        </div>
        <div id="meeting-factsheet-sync-hint" className="meeting-factsheet-sync-strip__hint" role="status">
          {intl.formatMessage({ id: syncHintId })}
        </div>
      </div>
      <div className="meeting-factsheet-sync-strip__actions">
        <Button
          size="small"
          variant="contained"
          color="primary"
          disableElevation
          aria-describedby="meeting-factsheet-sync-hint"
          disabled={syncState !== 'ready' || loading}
          onClick={syncMeetingNotesToFactsheet}
        >
          {intl.formatMessage({ id: syncActionId })}
        </Button>
      </div>
    </div>
  );

  const fieldsContent = (
    <>
      <div className={`row${focusField === 'latestMeetingDate' ? ' meeting-field-focus' : ''}`}>
        <div className="col-md-6 col-xs-6">
          <div className="form-group">
            <label className="control-label" htmlFor="input">
              {intl.formatMessage({ id: 'Latest Meeting' })}
            </label>
          </div>
        </div>
        <div className="col-md-6 col-xs-6 padding_left_zero">
          <div className="form-group datepicker-group left-side">
            <LatestMeetingDateControl
              className="date-picker"
              latestMeetingDate={latestMeetingDate}
              onChange={date =>
                toggleDialog('meeting', {
                  ...meetingDialog,
                  latestMeetingDate: date
                })
              }
            />
          </div>
        </div>
      </div>
      <div className="row">
        <div className="col-md-6 col-xs-6">
          <div className="form-group">
            <label className="control-label" htmlFor="input">
              {intl.formatMessage({ id: 'next-meeting-date' })}
            </label>
          </div>
        </div>
        <div className="col-md-6 col-xs-6 padding_left_zero">
          <div className="form-group datepicker-group left-side">
            <NextMeetingDateControl
              className="date-picker"
              nextMeetingDate={nextMeetingDate}
              onChange={date =>
                toggleDialog('meeting', {
                  ...meetingDialog,
                  nextMeetingDate: date
                })
              }
              client={client}
            />
          </div>
        </div>
      </div>
      <div className="row location">
        <div className="col-xs-6">
          <div className="form-group">
            <label className="control-label" htmlFor="input">
              {intl.formatMessage({ id: 'location' })}
            </label>
          </div>
        </div>
        <div className="col-xs-6 padding_left_zero">
          <div className="left-side">
            <LocationControl
              multiline={false}
              value={lastMeetingLocation}
              onChange={event =>
                toggleDialog('meeting', {
                  ...meetingDialog,
                  lastMeetingLocation: event.target.value
                })
              }
            />
          </div>
        </div>
      </div>
      <div className={`row${focusField === 'fsReminderDate' ? ' meeting-field-focus' : ''}`}>
        <div className={`col-md-6 col-xs-6`}>
          <div className="form-group">
            <label className="control-label" htmlFor="input" style={{ position: 'relative !important' }}>
              {intl.formatMessage({ id: 'Remind me' })}
            </label>
          </div>
        </div>
        <div className={`col-md-6 col-xs-6 padding_left_zero`}>
          <div className={`form-group datepicker-group left-side`}>
            <Grid container sx={{ flexDirection: 'column' }}>
              <Grid>
                <RemindDateControl
                  className="date-picker meeting-reminder-date"
                  fsReminderDate={fsReminderDate}
                  onChange={changeReminderDate}
                  minDate={moment()}
                  disabled={!fsReminderActive}
                  slots={{ day: ReminderLoadDay }}
                  slotProps={{ day: { load: reminderLoad, adviserEmail: userEmail } }}
                />
                <ReminderActiveControl
                  className="reminder-checkbox"
                  fsReminderActive={fsReminderActive}
                  onChange={changeReminderActive}
                />
              </Grid>
              {reminderDayLoad && (
                <Grid>
                  <ReminderLoadHint clients={reminderDayLoad} locale={intl.locale} formatMessage={intl.formatMessage} />
                </Grid>
              )}
              {reminderDateError && (
                <Grid>
                  <span className="error_field" style={{ fontSize: '13px' }}>
                    {intl.formatMessage({ id: reminderDateError })}
                  </span>
                </Grid>
              )}
            </Grid>
          </div>
        </div>
      </div>
      {/* Both reminder actions, on a line of their own under the date they act
          on. They used to be absolutely positioned beside the 提醒我 label —
          the tick at marginLeft 70, the calendar at 108 — while the date input
          starts around x=703, so both sat on top of the date the adviser had
          just set. A row of its own also gives them room for words: an icon
          alone left the reader to guess, and the tick in particular says
          nothing about todo lists.

          This also retires the widescreen split, where the same action was a
          text Button under 800px and a bare IconButton above it. One control,
          one label, every width. */}
      <div className="row">
        <div className="col-xs-12">
          <div className="reminder-date-actions">
            <Button
              className="reminder-date-add-todo-btn"
              variant="outlined"
              size="small"
              disabled={!reminderActionsEnabled}
              startIcon={<DoneOutlineRoundedIcon />}
              onClick={addToTodo}
            >
              {intl.formatMessage({ id: 'Add to Todo' })}
            </Button>
            {showSeeOnCalendar && (
              <Button
                className="reminder-date-calendar-btn"
                data-testid="reminder-see-on-calendar"
                variant="outlined"
                size="small"
                disabled={!reminderActionsEnabled}
                startIcon={<EventAvailableRoundedIcon />}
                onClick={seeOnCalendar}
              >
                {intl.formatMessage({ id: 'clientv2-cal-open-from-note' })}
              </Button>
            )}
          </div>
        </div>
      </div>
      <Grid
        container
        sx={{
          alignItems: 'center'
        }}
      >
        <Grid size={6}>
          <Typography style={{ color: '#333', fontSize: '14px' }}>
            {intl.formatMessage({ id: 'recruit-lead' })}
          </Typography>
        </Grid>
        <Grid size={6} style={{ textAlign: 'center' }}>
          <Checkbox
            name="isRecruitment"
            value="isRecruitment"
            checked={isRecruitment ?? false}
            onChange={event => toggleIsRecruitment(event.target.checked)}
            color="primary"
          />
        </Grid>
      </Grid>
      &nbsp;
      <div className="form-group" style={{ marginBottom: 0 }}>
        <TagsControl
          tags={tags}
          onChange={tags =>
            toggleDialog('meeting', {
              ...meetingDialog,
              tags: tags
            })
          }
          addTagButton={true}
        />
        <label className="control-label" htmlFor="input" style={{ top: '0px' }}>
          {intl.formatMessage({ id: 'Tag' })}
        </label>
        <i className="bar" />
      </div>
    </>
  );

  const content = (
    <div
      className="form-group"
      style={{ marginBottom: 0, height: '100%', display: 'flex', flexDirection: 'column' }}
      ref={ref}
    >
      {summaryMode && (
        <Fragment>
          <div className="case-close-met">
            <div className="col-md-6 col-xs-6 text-left caseclosing_padding">
              {intl.formatMessage({ id: 'No. of Meeting' })}
            </div>
            <MetControl
              className="col-md-6 col-xs-6"
              style={{ paddingLeft: '22px' }}
              met={met}
              onChange={met => toggleDialog('meeting', { ...meetingDialog, met: met })}
            />
          </div>
          &nbsp;
          <div className="row">
            <div className="col-md-6 col-xs-6 text-left fontWeight--none">
              {intl.formatMessage({ id: 'Opportunity' })}
            </div>
            <div className="col-md-6 col-xs-6 text-left adv_biz_modal_key">
              <div className="clientList">
                <OpportunityControl
                  opportunity={opportunity}
                  onChange={opp =>
                    toggleDialog('meeting', {
                      ...meetingDialog,
                      opportunity: opp
                    })
                  }
                />
              </div>
            </div>
          </div>
          <div className="row">
            <div className="col-md-6 col-xs-6 text-left fontWeight--none padding__left-0">
              {intl.formatMessage({ id: 'Expected Business' })}
            </div>
            <div className="col-md-6 col-xs-6">
              <BusinessVolControl
                style={{
                  borderBottom: '1px solid gray',
                  height: '25px',
                  lineHeight: 'normal',
                  color: '#3966f8'
                }}
                value={businessVol}
                onChange={value => {
                  setBusinessVolError(undefined);
                  toggleDialog('meeting', { ...meetingDialog, businessVol: value });
                }}
              />
              {businessVolError && <span className="error_field">{businessVolError}</span>}
            </div>
          </div>
        </Fragment>
      )}
      {summaryMode && (
        <>
          <div className="row datepicker-row">
            <div className="col-md-6 col-xs-6 text-left fontWeight--none">
              {intl.formatMessage({ id: 'Tracking start on' })}
            </div>
            <div className="col-md-6 col-xs-6 text-left">
              <DatePicker
                className="case-datepicker close-case-date case-open-datepicker tracking-start-datepicker"
                maxDate={moment()}
                name="startDate"
                format="DD-MMM-YYYY"
                value={trackingStartDate ? moment(trackingStartDate) : null}
                onChange={date =>
                  toggleDialog('meeting', {
                    ...meetingDialog,
                    trackingStartDate: date
                  })
                }
                inputProps={{ style: { fontSize: '14px' } }}
                style={{ width: '100%' }}
              />
            </div>
          </div>
          <div className="row">
            <div className="col-md-6 col-xs-6 text-left fontWeight--none">
              {intl.formatMessage({ id: 'day-count' })}
            </div>
            <div className="col-md-6 col-xs-6 day-count">
              <span>{`${
                trackingStartDate
                  ? moment(moment(), 'YYYY-MM-DD').diff(
                      moment.parseZone(trackingStartDate).format('YYYY-MM-DD'),
                      'days'
                    )
                  : 0
              } ${intl.formatMessage({ id: 'Days' })}`}</span>
            </div>
          </div>
        </>
      )}
      {!summaryMode &&
        (isWidescreen ? (
          <Box sx={{ display: 'flex', gap: 3, flexGrow: 1, minHeight: 0, height: '100%' }}>
            <Box sx={{ width: '60%', display: 'flex', flexDirection: 'column', height: '100%' }}>
              {remarkControlContent}
            </Box>
            <Box
              sx={{
                width: '40%',
                display: 'flex',
                flexDirection: 'column',
                overflowY: 'auto',
                overflowX: 'hidden',
                gap: 1,
                px: 2
              }}
            >
              {fieldsContent}
              {/* Last in the right column, i.e. under Tags: the strip acts on
                  the fields next to it, not on the note in the left column. */}
              {factsheetSyncStrip}
            </Box>
          </Box>
        ) : (
          <>
            <div style={{ display: 'flex', flexDirection: 'column', flexGrow: 1, marginBottom: 16 }}>
              {remarkControlContent}
            </div>
            {factsheetSyncStrip}
            {fieldsContent}
          </>
        ))}
    </div>
  );

  return (
    <Fragment>
      {/* Analysing is a wait with nothing to do in the dialog, and this sits at
          1400 — above the note form and the ToDo/Template drawers at 1350. */}
      <LoadingBackdrop open={loading || meetingFactsheetSyncing} />
      <BottomSheetOrDialog
        className="meeting-dialog"
        open={!!meetingDialog && !closed}
        onClose={close}
        onExited={onExited}
        header={header}
        headerCTAs={[]}
        content={content}
        actions={actions}
        actionsPrepend={actionsPrepend}
        BottomSheetProps={{
          snapPoints: ({ minHeight, maxHeight }) => (!summaryMode ? maxHeight : minHeight),
          defaultSnap: ({ lastSnap, snapPoints }) => lastSnap ?? Math.max(...snapPoints),
          expandOnContentDrag: false,
          disableAutoDismiss: true,
          BoxProps: {
            height: !summaryMode ? `calc(${innerHeight}px - 54px - 65px - var(--top-padding))` : undefined
          }
        }}
        DialogProps={{
          fullWidth: true,
          maxWidth: !summaryMode && isWidescreen ? 'md' : 'sm',
          PaperProps: {
            style: !summaryMode
              ? {
                  height: '100%'
                }
              : undefined
          }
        }}
        DialogContentProps={
          !summaryMode && isWidescreen
            ? {
                style: {
                  overflow: 'hidden',
                  display: 'flex',
                  flexDirection: 'column',
                  height: '100%',
                  minHeight: 0
                }
              }
            : undefined
        }
      />
      <ChooseDefaultCalendarDialog
        open={chooserDefaultCalendarDialogOpen}
        onClose={() => {
          setChooserDefaultCalendarDialogOpen(false);
          submit();
        }}
      />
      <SwipeableDrawer
        anchor="right"
        variant={innerWidth > 800 ? 'persistent' : 'temporary'}
        onClose={closeTodo}
        onOpen={openTodo}
        open={todoOpen}
        disableBackdropTransition={!iOS}
        disableDiscovery={iOS}
        disableSwipeToOpen={true}
        className="client-page-filter-SwipeableDrawer"
        sx={{ zIndex: MEETING_DRAWER_Z_INDEX }}
        ModalProps={DRAWER_MODAL_PROPS}
        slotProps={{
          paper: { elevation: 10, style: { zIndex: MEETING_DRAWER_Z_INDEX } }
        }}
      >
        <ThemeProvider theme={raiseModalLayerAboveDrawer}>
          <TodoList close={closeTodo} openTemplate={openTemplate} />
        </ThemeProvider>
      </SwipeableDrawer>
      <SwipeableDrawer
        anchor="right"
        variant={innerWidth > 800 ? 'persistent' : 'temporary'}
        onClose={closeTemplate}
        onOpen={openTemplate}
        open={templateOpen}
        disableBackdropTransition={!iOS}
        disableDiscovery={iOS}
        disableSwipeToOpen={true}
        className="client-page-filter-SwipeableDrawer"
        sx={{ zIndex: MEETING_DRAWER_Z_INDEX }}
        ModalProps={DRAWER_MODAL_PROPS}
        slotProps={{
          paper: { elevation: 10, style: { zIndex: MEETING_DRAWER_Z_INDEX } }
        }}
      >
        <ThemeProvider theme={raiseModalLayerAboveDrawer}>
          <Template close={closeTemplate} />
        </ThemeProvider>
      </SwipeableDrawer>
    </Fragment>
  );
};

const container = connect(
  // Map state to props
  state => ({
    userDetails: state.user.userDetails,
    subscriptions: state.userSubscription.subscriptions,
    meetingDialog: state.control.meetingDialog,
    myWhiteBoardDialog: state.control.myWhiteBoardDialog,
    createClientPhase: state.client.createClientPhase,
    getClientByIdPhase: state.client.getClientByIdPhase,
    getUserPhase: state.client.getUserPhase,
    teamCampaignCodes: state.functions.campaignCodes,
    userSubscription: state.userSubscription.userSubscription,
    clientData: state.client.clientData
  }), // Map actions to props
  {
    toggleDialog,
    mergeDialog,
    enableTracking,
    submitClient,
    initCreatePhase,
    addAdviserTodo
  }, // mergeProps
  null, // options
  {}
)(withRouter(MeetingDialog));

export default injectIntl(withWidth()(container));
